import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Upload,
  Trash2,
  Image as ImageIcon,
  Loader2,
  X,
  Archive,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";

function ZipImportButton({ subType, onImported }) {
  const inputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);

  const onFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Trebuie un fișier .zip");
      return;
    }
    if (file.size > 150 * 1024 * 1024) {
      toast.error(
        `ZIP-ul are ${(file.size / 1024 / 1024).toFixed(0)} MB. Limita este 150 MB per upload. Împarte ZIP-ul pe stiluri (ART DECO / CLASIC / MODERN / NEOCLASIC) și încarcă-le pe rând.`,
        { duration: 12000 }
      );
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/catalog/import-zip/${subType}`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 600000, // 10 minutes for large ZIPs
        }
      );
      setReport(data);
      toast.success(
        `${data.uploaded_count} imagini importate · ${data.skipped_duplicates} duplicate sărite`
      );
      if (onImported) onImported();
    } catch (e) {
      const status = e.response?.status;
      const det = e.response?.data?.detail;
      let msg = formatApiErrorDetail(det) || "Eroare import ZIP";
      if (status === 502 || status === 413 || e.code === "ERR_NETWORK") {
        msg =
          "ZIP-ul e prea mare pentru un singur upload (limita ~150 MB). Împarte ZIP-ul pe stiluri (ex: separat ART DECO, CLASIC, MODERN, NEOCLASIC) și încarcă fiecare arhivă pe rând.";
      }
      toast.error(msg, { duration: 12000 });
    } finally {
      setImporting(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={importing}
        className="bg-orange-500 hover:bg-orange-600 text-white"
        data-testid={`zip-import-${subType.toLowerCase()}`}
      >
        {importing ? (
          <>
            <Loader2 className="animate-spin mr-2" size={14} />
            Se importă...
          </>
        ) : (
          <>
            <Archive size={14} className="mr-2" />
            Import ZIP {subType === "INTERIOR" ? "Interior" : "Exterior"}
          </>
        )}
      </Button>
      <p className="text-[10px] text-zinc-500 max-w-xs text-right leading-relaxed">
        Structură: <span className="text-zinc-300">{subType}/STIL/CATEGORIE/cod_xxx.png</span>
        <br />
        Max 150 MB/upload — împarte pe stiluri dacă e mai mare.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept=".zip"
        onChange={(e) => onFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`zip-input-${subType.toLowerCase()}`}
      />
      <Dialog open={!!report} onOpenChange={(o) => !o && setReport(null)}>
        <DialogContent className="max-w-xl bg-zinc-950 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-outfit font-bold">
              Raport import ZIP {subType.toLowerCase()}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Sumar al fișierelor procesate.
            </DialogDescription>
          </DialogHeader>
          {report && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3">
                  <div className="flex items-center gap-2 text-emerald-400">
                    <CheckCircle2 size={14} />
                    <span className="font-bold">Importate</span>
                  </div>
                  <div className="text-2xl font-outfit font-bold text-white mt-1">
                    {report.uploaded_count}
                  </div>
                </div>
                <div className="rounded-md border border-zinc-500/30 bg-zinc-500/10 p-3">
                  <div className="text-zinc-400 font-bold">Sărite</div>
                  <div className="text-2xl font-outfit font-bold text-white mt-1">
                    {report.skipped_duplicates +
                      report.skipped_invalid +
                      report.skipped_too_large}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    {report.skipped_duplicates} duplicate ·{" "}
                    {report.skipped_invalid} invalide ·{" "}
                    {report.skipped_too_large} prea mari
                  </div>
                </div>
              </div>
              {Object.keys(report.by_category || {}).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">
                    Repartizare pe categorii
                  </div>
                  <div className="rounded-md border border-white/5 bg-zinc-900/40 max-h-48 overflow-y-auto">
                    {Object.entries(report.by_category).map(([slug, n]) => (
                      <div
                        key={slug}
                        className="flex justify-between px-3 py-1.5 text-xs border-b border-white/5 last:border-0"
                      >
                        <span className="text-zinc-300">{slug}</span>
                        <span className="text-orange-400 font-bold">{n}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {report.unmatched_folders?.length > 0 && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
                  <div className="flex items-center gap-2 text-amber-400 mb-1.5">
                    <AlertTriangle size={14} />
                    <span className="font-bold text-xs uppercase tracking-wider">
                      Foldere neîmperecheate ({report.unmatched_folders.length})
                    </span>
                  </div>
                  <div className="text-xs text-zinc-300 space-y-0.5 max-h-32 overflow-y-auto">
                    {report.unmatched_folders.map((f) => (
                      <div key={f}>• {f}</div>
                    ))}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-2">
                    Redenumește aceste foldere ca să corespundă numelor
                    categoriilor FDP și reimportă.
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => setReport(null)}
              className="border-white/10 text-zinc-300 hover:bg-white/5"
              data-testid="zip-report-close"
            >
              Închide
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StyleChips({ category, simType, onChanged }) {
  const styles = ["ART_DECO", "CLASSIC", "MODERN", "NEOCLASSIC"];
  const [savingStyle, setSavingStyle] = useState(null);
  // Tracks the optimistic local state per style on top of the prop
  const [localHidden, setLocalHidden] = useState(() =>
    Object.fromEntries(
      styles.map((s) => [s, (category.hidden_styles || []).includes(s)]),
    ),
  );
  useEffect(() => {
    setLocalHidden(
      Object.fromEntries(
        styles.map((s) => [s, (category.hidden_styles || []).includes(s)]),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const toggle = async (e, style) => {
    e.stopPropagation();
    const next = !localHidden[style];
    setSavingStyle(style);
    setLocalHidden((prev) => ({ ...prev, [style]: next }));
    try {
      await api.patch("/admin/catalog/style-visibility", {
        sim_type: simType,
        style,
        category_slug: category.slug,
        is_hidden: next,
      });
      toast.success(
        next
          ? `«${category.name}» arhivat pentru ${style.replace("_", " ")}`
          : `«${category.name}» reactivat pentru ${style.replace("_", " ")}`,
      );
      if (onChanged) onChanged();
    } catch (e2) {
      setLocalHidden((prev) => ({ ...prev, [style]: !next }));
      toast.error(
        formatApiErrorDetail(e2.response?.data?.detail) ||
          "Nu am putut actualiza arhivarea per stil",
      );
    } finally {
      setSavingStyle(null);
    }
  };

  return (
    <div className="px-4 pb-3">
      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
        Vizibilitate per stil
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {styles.map((s) => {
          const hidden = !!localHidden[s];
          const count =
            (category.photos_by_style && category.photos_by_style[s]) || 0;
          const saving = savingStyle === s;
          return (
            <button
              key={s}
              type="button"
              disabled={saving}
              onClick={(e) => toggle(e, s)}
              className={`text-[10px] uppercase tracking-[0.12em] font-bold py-1.5 px-1.5 rounded border transition flex items-center justify-between gap-1.5 ${
                hidden
                  ? "border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20"
                  : "border-white/10 bg-zinc-900/40 text-zinc-300 hover:border-orange-500/40 hover:bg-zinc-900/70"
              } ${saving ? "opacity-50 cursor-wait" : ""}`}
              data-testid={`style-toggle-${category.slug}-${s}`}
              title={
                hidden
                  ? `Click pentru a reactiva categoria pentru ${s.replace("_", " ")}`
                  : `Click pentru a arhiva categoria pentru ${s.replace("_", " ")}`
              }
            >
              <span className="truncate">
                {s.replace("_", " ")}
                <span className="text-zinc-500 ml-1 normal-case">
                  ({count})
                </span>
              </span>
              {saving ? (
                <Loader2 size={9} className="animate-spin" />
              ) : hidden ? (
                <EyeOff size={9} />
              ) : (
                <Eye size={9} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CategoryCard({ category, simType, onOpen, onVisibilityChange }) {
  const count = category.uploaded_photos_count || 0;
  const isHidden = !!category.is_hidden;
  const [toggling, setToggling] = useState(false);

  const toggleVisibility = async (e) => {
    e.stopPropagation(); // Don't open the dialog
    setToggling(true);
    try {
      const { data } = await api.patch("/admin/catalog/visibility", {
        sim_type: simType,
        category_slug: category.slug,
        is_hidden: !isHidden,
      });
      toast.success(
        data.is_hidden
          ? `«${category.name}» ascuns pentru utilizatori`
          : `«${category.name}» vizibil pentru utilizatori`,
      );
      if (onVisibilityChange) onVisibilityChange();
    } catch (e2) {
      toast.error(
        formatApiErrorDetail(e2.response?.data?.detail) ||
          "Nu am putut schimba vizibilitatea categoriei",
      );
    } finally {
      setToggling(false);
    }
  };

  return (
    <div
      className={`group relative text-left rounded-lg border bg-zinc-900/30 transition ${
        isHidden
          ? "border-red-500/30 ring-1 ring-red-500/20"
          : "border-white/10 hover:border-orange-500/50 hover:bg-zinc-900/60"
      }`}
      data-testid={`catalog-card-${category.slug}`}
    >
      {/* Clickable area opens the photos dialog */}
      <button
        type="button"
        onClick={() => onOpen(category)}
        className="w-full text-left p-4 block"
      >
        <div className="aspect-video w-full rounded-md overflow-hidden bg-zinc-950/60 mb-3 flex items-center justify-center relative">
          {category.image ? (
            <img
              src={category.image}
              alt={category.name}
              className={`w-full h-full object-cover group-hover:scale-105 transition-all duration-500 ${
                isHidden ? "grayscale brightness-50" : ""
              }`}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <ImageIcon size={32} className="text-zinc-700" />
          )}
          {isHidden && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[1px]">
              <span className="bg-red-500/20 border border-red-500/40 text-red-300 text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded font-bold flex items-center gap-1.5">
                <EyeOff size={12} /> Ascuns
              </span>
            </div>
          )}
        </div>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className={`font-outfit font-bold text-sm truncate ${isHidden ? "text-zinc-400" : "text-white"}`}>
              {category.name}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
              {category.count} produse catalog
            </div>
          </div>
          <div
            className={`shrink-0 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${
              count > 0
                ? isHidden ? "bg-zinc-700/40 text-zinc-400" : "bg-orange-500/20 text-orange-400"
                : "bg-white/5 text-zinc-500"
            }`}
            data-testid={`catalog-count-${category.slug}`}
          >
            {count} urcate
          </div>
        </div>
      </button>
      {/* Visibility toggle — separated so it doesn't open the dialog */}
      <div className="px-4 pb-3">
        <button
          type="button"
          onClick={toggleVisibility}
          disabled={toggling}
          className={`w-full text-[10px] uppercase tracking-[0.18em] font-bold py-2 rounded border transition flex items-center justify-center gap-1.5 ${
            isHidden
              ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              : "border-white/10 text-zinc-400 hover:border-red-500/30 hover:text-red-400 hover:bg-red-500/5"
          } ${toggling ? "opacity-50 cursor-wait" : ""}`}
          data-testid={`toggle-visibility-${category.slug}`}
          title={
            isHidden
              ? "Categoria este ascunsă utilizatorilor. Click pentru a o afișa."
              : "Categoria este vizibilă utilizatorilor. Click pentru a o ascunde."
          }
        >
          {toggling ? (
            <Loader2 size={11} className="animate-spin" />
          ) : isHidden ? (
            <Eye size={11} />
          ) : (
            <EyeOff size={11} />
          )}
          {isHidden ? "Arată utilizatorilor" : "Ascunde pentru utilizatori"}
        </button>
      </div>
      <StyleChips
        category={category}
        simType={simType}
        onChanged={onVisibilityChange}
      />
    </div>
  );
}

function CategoryDialog({ category, onClose, onChange }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    if (!category) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/catalog/${category.slug}/photos`
      );
      setPhotos(data.photos || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (!files.length) {
      toast.error("Selectează imagini (PNG/JPG/WEBP)");
      return;
    }
    if (files.length > 20) {
      toast.error("Maxim 20 imagini per upload");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(
        `/admin/catalog/${category.slug}/photos`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        `${data.uploaded.length} ${
          data.uploaded.length === 1 ? "imagine urcată" : "imagini urcate"
        }`
      );
      await fetchPhotos();
      if (onChange) onChange();
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Eroare upload"
      );
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (photoId) => {
    if (!window.confirm("Sigur ștergi această fotografie?")) return;
    try {
      await api.delete(`/admin/catalog/photos/${photoId}`);
      toast.success("Fotografie ștearsă");
      await fetchPhotos();
      if (onChange) onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
  };

  return (
    <Dialog open={!!category} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto bg-zinc-950 border-white/10 text-white"
        data-testid="catalog-upload-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-outfit font-bold text-white">
            {category?.name}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Urcă fotografii ale produselor din această categorie. Format
            recomandat: PNG / JPG / WEBP, max 10MB per imagine.
          </DialogDescription>
        </DialogHeader>

        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            upload(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          className={`rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition ${
            dragOver
              ? "border-orange-500 bg-orange-500/10"
              : "border-white/15 bg-zinc-900/30 hover:border-white/30"
          }`}
          data-testid="catalog-dropzone"
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-3 text-zinc-300">
              <Loader2 className="animate-spin" size={20} />
              <span>Se urcă...</span>
            </div>
          ) : (
            <>
              <Upload
                size={36}
                className="mx-auto text-orange-500 mb-2"
                strokeWidth={1.5}
              />
              <div className="font-outfit font-bold text-white">
                Trage fotografiile aici sau click
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                PNG, JPG, WEBP · max 10MB per imagine · până la 20 odată
              </div>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => upload(e.target.files)}
            className="hidden"
            data-testid="catalog-file-input"
          />
        </div>

        {/* Photos grid */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">
              Fotografii urcate
            </div>
            <div className="text-xs text-zinc-400">
              {photos.length} {photos.length === 1 ? "fotografie" : "fotografii"}
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8 text-zinc-500">
              <Loader2 className="animate-spin" size={22} />
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 text-sm border border-white/5 rounded-md bg-zinc-900/20">
              Nicio fotografie încă.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {photos.map((p) => (
                <div
                  key={p.id}
                  className="group relative aspect-square rounded-md overflow-hidden border border-white/10 bg-zinc-900/40"
                  data-testid={`catalog-photo-${p.id}`}
                >
                  <img
                    src={fileUrl(p.url)}
                    alt={p.original_filename || ""}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(p.id)}
                    className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full bg-black/70 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    title="Șterge"
                    data-testid={`catalog-delete-${p.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                  {p.original_filename && (
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <div className="text-[10px] text-white/80 truncate">
                        {p.original_filename}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-white/10 text-zinc-300 hover:bg-white/5"
            data-testid="catalog-dialog-close"
          >
            <X size={14} className="mr-1" /> Închide
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CatalogManager() {
  const [catalog, setCatalog] = useState(null);
  const [active, setActive] = useState(null); // active category for modal
  const [loading, setLoading] = useState(true);
  const [subType, setSubType] = useState("INTERIOR");

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/catalog");
      setCatalog(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  if (loading || !catalog) {
    return (
      <div className="flex items-center justify-center py-16 text-zinc-500">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  const cats = catalog[subType]?.categories || [];
  const totalUploaded = cats.reduce(
    (sum, c) => sum + (c.uploaded_photos_count || 0),
    0
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1 p-1 rounded-lg border border-white/10 bg-zinc-900/40">
          <button
            type="button"
            onClick={() => setSubType("INTERIOR")}
            className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition ${
              subType === "INTERIOR"
                ? "bg-orange-500 text-white"
                : "text-zinc-400 hover:text-white"
            }`}
            data-testid="catalog-tab-interior"
          >
            Interior · {catalog.INTERIOR?.categories?.length || 0}
          </button>
          <button
            type="button"
            onClick={() => setSubType("EXTERIOR")}
            className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition ${
              subType === "EXTERIOR"
                ? "bg-orange-500 text-white"
                : "text-zinc-400 hover:text-white"
            }`}
            data-testid="catalog-tab-exterior"
          >
            Exterior · {catalog.EXTERIOR?.categories?.length || 0}
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-zinc-500">
            Fotografii urcate pe {subType === "INTERIOR" ? "interior" : "exterior"}:{" "}
            <span className="text-orange-400 font-bold">{totalUploaded}</span>
          </div>
          <CatalogSyncButton onSynced={fetchCatalog} />
          <CatalogDedupButton onDeduped={fetchCatalog} />
          <ZipImportButton subType={subType} onImported={fetchCatalog} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {cats.map((c) => (
          <CategoryCard
            key={c.slug}
            category={c}
            simType={subType}
            onOpen={setActive}
            onVisibilityChange={fetchCatalog}
          />
        ))}
      </div>

      <CategoryDialog
        category={active}
        onClose={() => setActive(null)}
        onChange={fetchCatalog}
      />
    </div>
  );
}

// ---------------------------------------------------------------- Catalog Sync (preview → production)
function CatalogSyncButton({ onSynced }) {
  const [open, setOpen] = React.useState(false);
  const [sourceUrl, setSourceUrl] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const runDryRun = async () => {
    if (!sourceUrl.trim().startsWith("http")) {
      toast.error("URL invalid — trebuie să înceapă cu http(s)://");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/catalog/sync-from-url", {
        source_url: sourceUrl.trim().replace(/\/$/, ""),
        dry_run: true,
      });
      setPreview(data);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Eroare conectare la sursă"
      );
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const runSync = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/catalog/sync-from-url", {
        source_url: sourceUrl.trim().replace(/\/$/, ""),
        dry_run: false,
      });
      toast.success(
        `${data.imported} importate · ${data.skipped_dedup} sărite · ${data.failed} eșuate`,
        { duration: 9000 }
      );
      setOpen(false);
      setPreview(null);
      if (onSynced) onSynced();
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Eroare sync"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500"
        data-testid="catalog-sync-open-btn"
      >
        Sync din alt mediu
      </Button>
      <Dialog open={open} onOpenChange={(v) => !loading && setOpen(v)}>
        <DialogContent
          className="bg-zinc-950 border-white/10 max-w-xl"
          data-testid="catalog-sync-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white">
              Sincronizare catalog din alt mediu
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-amber-200 text-xs leading-relaxed">
              <strong>Util pentru a copia catalogul din preview în producție.</strong>
              <br />
              Introdu URL-ul mediului-sursă (ex: preview), apasă „Verifică" și
              apoi „Sincronizează acum". Sincronizarea sare automat
              fotografiile deja prezente (dedup pe SHA256).
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1 block">
                URL mediu sursă
              </Label>
              <Input
                type="url"
                placeholder="https://render-profile.preview.emergentagent.com"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                className="bg-zinc-900 border-white/10 text-white"
                data-testid="catalog-sync-source-url"
              />
            </div>
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={runDryRun}
                disabled={loading || !sourceUrl.trim()}
                className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                data-testid="catalog-sync-preview-btn"
              >
                {loading ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : null}
                Verifică (dry-run)
              </Button>
            </div>
            {preview && (
              <div
                className="rounded border border-white/10 bg-zinc-900/50 p-4 grid grid-cols-3 gap-3 text-center"
                data-testid="catalog-sync-preview"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total sursă</div>
                  <div className="text-2xl font-bold text-zinc-200 mt-1">
                    {preview.source_total}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">De importat</div>
                  <div className="text-2xl font-bold text-emerald-400 mt-1">
                    {preview.would_import}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Deja existente</div>
                  <div className="text-2xl font-bold text-zinc-400 mt-1">
                    {preview.would_skip_dedup}
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={loading}
                className="text-zinc-400 hover:text-white"
                data-testid="catalog-sync-cancel-btn"
              >
                Anulează
              </Button>
              <Button
                onClick={runSync}
                disabled={loading || !preview || preview.would_import === 0}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="catalog-sync-confirm-btn"
              >
                {loading ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : null}
                Sincronizează {preview?.would_import || 0} fotografii
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * One-click "Cleanup duplicates" button. Used to recover after a
 * double-sync or import-then-sync flow that produced multiple rows with
 * the same (sim_type, style, slug, product_code, original_filename) but
 * different SHA256 (because Pillow re-encodes on import-zip while
 * sync-from-url transfers the already-encoded bytes).
 *
 * Always does a dry-run first so admin sees how many will be removed
 * before committing.
 */
function CatalogDedupButton({ onDeduped }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/catalog/dedup?dry_run=true");
      setPreview(data);
      if (data.would_delete_count === 0) {
        toast.success("Nicio poză duplicată găsită — catalog curat!");
      } else {
        toast.message(
          `${data.would_delete_count} poze duplicate găsite în ${data.groups_with_duplicates} grupuri.`,
        );
      }
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Eroare preview dedup",
      );
    } finally {
      setLoading(false);
    }
  };

  const runDelete = async () => {
    if (!preview || preview.would_delete_count === 0) return;
    if (!window.confirm(
      `Sigur ștergi ${preview.would_delete_count} poze duplicate?\n\nPentru fiecare grup păstrez cea mai veche încărcare și șterg copiile mai noi.`,
    )) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/catalog/dedup?dry_run=false");
      toast.success(`${data.deleted_count} poze duplicate șterse.`);
      setOpen(false);
      setPreview(null);
      onDeduped && onDeduped();
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Eroare la ștergere",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
        onClick={() => {
          setOpen(true);
          setPreview(null);
          runPreview();
        }}
        data-testid="catalog-dedup-open-btn"
      >
        <Layers size={13} className="mr-1.5" /> Curăță duplicate
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-zinc-950 border-amber-500/30 max-w-2xl max-h-[80vh] overflow-y-auto"
          data-testid="catalog-dedup-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white font-outfit flex items-center gap-2">
              <Layers className="text-amber-400" size={18} />
              Curăță duplicate catalog
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Identific poze cu aceeași (sim_type, stil, slug, cod produs, filename) dar SHA256 diferit. Le păstrez pe cea mai veche și șterg restul. Util după sync dublu sau import-zip + sync.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="py-8 flex items-center justify-center text-zinc-500">
              <Loader2 className="animate-spin mr-2" size={18} />
              Se procesează...
            </div>
          )}

          {!loading && preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-white/10 bg-zinc-950/40 p-3 text-center">
                  <div className="text-2xl font-black font-outfit text-white">
                    {preview.groups_with_duplicates}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">
                    Grupuri cu duplicate
                  </div>
                </div>
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-center">
                  <div className="text-2xl font-black font-outfit text-amber-400">
                    {preview.would_delete_count}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">
                    Poze de șters
                  </div>
                </div>
              </div>

              {preview.summary && preview.summary.length > 0 && (
                <div className="rounded-md border border-white/10 bg-zinc-950/40 p-3 max-h-64 overflow-y-auto">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                    Primele {preview.summary.length} grupuri:
                  </div>
                  {preview.summary.map((g) => (
                    <div
                      key={`${g.key.sim_type}-${g.key.style}-${g.key.category_slug}-${g.key.product_code}-${g.key.original_filename}`}
                      className="text-xs text-zinc-300 py-1.5 border-b border-white/5 last:border-b-0"
                    >
                      <div className="font-mono text-[11px]">
                        {g.key.sim_type} / {g.key.style} / {g.key.category_slug} / <span className="text-orange-400">{g.key.product_code || "—"}</span> · <span className="text-zinc-500">{g.key.original_filename}</span>
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {g.total}x · păstrez 1, șterg {g.dropped_ids.length}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {preview.would_delete_count > 0 ? (
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="ghost"
                    onClick={() => setOpen(false)}
                    className="text-zinc-400"
                    data-testid="catalog-dedup-cancel"
                  >
                    Anulează
                  </Button>
                  <Button
                    onClick={runDelete}
                    disabled={loading}
                    className="bg-rose-500 hover:bg-rose-600 text-white"
                    data-testid="catalog-dedup-confirm"
                  >
                    <Trash2 size={13} className="mr-1.5" />
                    Șterge {preview.would_delete_count} poze duplicate
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm py-2">
                  <CheckCircle2 size={16} />
                  <span>Catalogul este curat — nicio acțiune necesară.</span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
